import { chatTranslations } from '@glade-ai/chat-plugin/ui';
import { I18nProvider } from '@glade-ai/ui';
import { useRouter } from 'next/router';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { AuthContext } from '@/providers/Auth';
import { useLocalStorageState } from '@/providers/LocalStorageState';
import { mixpanelTrack } from '@/providers/Mixpanel';

import { logError } from '../ErrorTracking';

import {
  DEFAULT_LANGUAGE,
  DEFAULT_NS,
  type LanguageCode,
  SUPPORTED_LANGUAGE_CODES,
  isEnglishVariant,
} from './constants';
import handleTranslate from './handleTranslate';
import languageSerialization from './languageSerialization';
import TranslationContext from './TranslationContext';
import validateLanguageCode from './validateLanguageCode';

const i18nResources = {
  [DEFAULT_LANGUAGE.code]: {
    ...chatTranslations[DEFAULT_LANGUAGE.code],
    [DEFAULT_NS]: {
      // TODO(will): Once `Intl.DurationFormat` has broad availability, these should become deprecated.
      durationInMinutes_one: '{{count}} minute',
      durationInMinutes_other: '{{count}} minutes',
      'interval--narrow--DAY': 'dy',
      'interval--narrow--MONTH': 'mo',
      'interval--narrow--SIXMONTHS': '6 mo’s',
      'interval--narrow--THREEMONTHS': '3 mo’s',
      'interval--narrow--WEEK': 'wk',
      'interval--narrow--YEAR': 'yr',
      priceInterval: '{{price}}/{{interval}}',
      priceWithCurrency: '{{price, currency}}',
      'priceWithCurrency--FREE': 'free',
      'sessionDuration--DAY': 'All day',
      'sessionDuration--NONE': 'One time',
      // chat
      'chat.replyCount_one': '{{count}} reply',
      'chat.replyCount_other': '{{count}} replies',
      'chat.unknownUser': 'Unknown',
      // util
      intlList: '{{value, list}}',
      // forms
      'forms.inputs.password.label': 'Password',
      'forms.inputs.password.placeholder': 'Enter your password',
      'forms.inputs.password.show': 'Show password',
      'forms.inputs.password.hide': 'Hide password',
      'forms.inputs.language.label': 'Your preferred language',
      'forms.inputs.country.label': 'Country',
      // pages
      // UserProfile Home
      'userProfile.navMenu.main.label': 'Main menu',
      'userProfile.navMenu.user.label': 'Your profile and organization',
      'userProfile.navMenu.user.edit.label': 'Your profile',
      'userProfile.navMenu.organization.manage.label': 'Manage {{name}}',
      'userProfile.navMenu.organization.manage.label--generic': 'Manage organization',
      'userProfile.navMenu.organization.create.label': 'Create organization',
      'userProfile.home.empty.title': 'Checking your account for updates…',
      'userProfile.home.empty.description': 'This should only take a moment.',
      'userProfile.home.balance.invoices_one': '{{count}} Invoice',
      'userProfile.home.balance.invoices_other': '{{count}} Invoices',
      'userProfile.home.balance.none': 'No unpaid invoices',
      'userProfile.navMenu.main.workflows.label': 'Your workflows',
      'userProfile.navMenu.user.logout.label': 'Log out',
      'userProfile.workflowPage.title--loading': 'Workflow loading…',
      // Activity widget relative time
      'activity.timeAgo': '{{timeDelta}} ago',
      // Activity event templates (using Trans component with <black>, <gray>, and <blue> components)
      'activity.meansTestAgentRun':
        '<black>{{userName}}</black> <gray>ran means test agent on</gray> <black>{{documentTitle}}</black>',
      'activity.exemptionsAgentRun':
        '<black>{{userName}}</black> <gray>ran exemptions agent on</gray> <black>{{questionnaireName}}</black>',
      'activity.documentUploaded': '<black>{{userName}}</black> <gray>uploaded</gray> <black>{{fileName}}</black>',
      'activity.paymentSucceeded':
        '<black>{{userName}}</black> <gray>succeeded {{type}} payment for</gray> <black>{{name}}</black>',
      'activity.paymentFailed':
        '<black>{{userName}}</black> <gray>failed {{type}} payment for</gray> <black>{{name}}</black>',
      'activity.paymentInitiated':
        '<black>{{userName}}</black> <gray>initiated {{type}} payment for</gray> <black>{{name}}</black>',
      'activity.ownerAssigned': '<black>{{displayText}}</black>',
      'activity.ownerUnassigned': '<black>{{displayText}}</black>',
      'activity.commentLeft': '<black>{{userName}}</black> <gray>sent a message to</gray> <black>discussion</black>',
      'activity.userWorkflowStatusChange':
        '<black>{{userName}}</black> <gray>changed the workflow status to</gray> <blue>{{newStatus}}</blue>',
      'activity.userWorkflowInitiated': '<black>{{userName}}</black> <gray>initiated workflow</gray>',
      'activity.documentRequestCompleted':
        '<black>{{userName}}</black> <gray>completed</gray> <black>{{documentTitle}}</black> <gray>document request</gray>',
      'activity.documentRequestSubmittedForReview':
        '<black>{{userName}}</black> <gray>submitted</gray> <black>{{documentTitle}}</black> <gray>document request for review</gray>',
      'activity.termsAgreed':
        '<black>{{userName}}</black> <gray>signed to</gray> <black>{{documentName}}</black> <gray>terms</gray>',
      'activity.formRequestCompleted': '<black>{{userName}}</black> <gray>completed</gray> <black>{{formName}}</black>',
      'activity.noteAdded': '<black>{{userName}}</black> <gray>added a note to</gray> <black>discussion</black>',
      'activity.dataImported':
        '<black>{{userName}}</black> <gray>imported data from</gray> <black>{{sourceName}}</black> <gray>into</gray> <black>{{targetQuestionnaireName}}</black>',
      'activity.dataImportedNoTarget':
        '<black>{{userName}}</black> <gray>imported data from</gray> <black>{{sourceName}}</black>',
      // PACER document selection
      'pacer.addingDocuments': 'Adding {{count}} document...',
      'pacer.addingDocuments_other': 'Adding {{count}} documents...',
      'pacer.assignFileNames_one': 'Assign file names ({{count}} document)',
      'pacer.assignFileNames_other': 'Assign file names ({{count}} documents)',
      'pacer.addToPacket_one': 'Add {{count}} document to PACER packet',
      'pacer.addToPacket_other': 'Add {{count}} documents to PACER packet',
      // PACER pre-filing warnings
      'pacer.prefiling.sectionLabel.courtNoticeMatch': 'Court Notice Match',
      'pacer.prefiling.sectionLabel.existingCaseNumber': 'Existing Case Number',
      'pacer.prefiling.sectionLabel.recentFilingAttempt': 'Recent Filing Attempt',
      'pacer.prefiling.existingCaseNumber.title': 'Duplicate Filing Warning',
      'pacer.prefiling.existingCaseNumber.description':
        'A case number already exists for this workflow. Please review carefully before proceeding.',
      'pacer.prefiling.existingCaseNumber.checkbox':
        'I understand the risks and proceeding may create a duplicate filing.',
      'pacer.prefiling.courtNoticeMatch.title': 'Recent Court Notice Match',
      'pacer.prefiling.courtNoticeMatch.description':
        'A court notice was recently received that may match this client. Please verify in PACER before proceeding.',
      'pacer.prefiling.courtNoticeMatch.checkbox':
        'I have verified in PACER whether a case was already created for this workflow.',
      'pacer.prefiling.recentFilingAttempt.title': 'Recent Filing Attempt',
      'pacer.prefiling.recentFilingAttempt.description':
        'A filing was recently attempted for this workflow. Please verify in PACER before proceeding.',
      'pacer.prefiling.recentFilingAttempt.checkbox':
        'I have verified in PACER whether a case was already created for this workflow.',
      'pacer.prefiling.status.succeeded': 'Successfully filed',
      'pacer.prefiling.status.failed': 'Previous attempt failed',
      'pacer.prefiling.status.cancelled': 'Filing was cancelled',
      'pacer.prefiling.status.inProgress': 'Currently being filed',
      'pacer.prefiling.status.manual': 'Manually recorded',
      'pacer.prefiling.risk.cancelled': 'Filing was cancelled before completion',
      'pacer.prefiling.risk.succeeded': 'Duplicate filing in bankruptcy court',
      'pacer.prefiling.risk.failed': 'Case may have been partially created',
      'pacer.prefiling.risk.inProgress': 'Duplicate filing if submitted again',
      'pacer.prefiling.risk.manual': 'Duplicate filing in bankruptcy court',
      'pacer.ecfModal.preValidation.inProgress': 'A filing is already in progress for this case.',
      'pacer.ecfModal.preValidation.existingCaseNumber':
        'This case already has case number {{caseNumber}}. Filing has been blocked.',
      'pacer.ecfModal.preValidation.checkFailed': 'Unable to verify filing status. Please try again.',
      'pacer.prefiling.attemptStatus.cancelled': 'Cancelled',
      'pacer.prefiling.attemptStatus.succeeded': 'Succeeded',
      'pacer.prefiling.attemptStatus.failed': 'Failed',
      'pacer.prefiling.attemptStatus.inProgress': 'In progress',
      'pacer.prefiling.attemptStatus.manual': 'Manual',
      'pacer.prefiling.existingCaseNumber.hardBlockDescription':
        'This workflow already has case number {{caseNumber}}. Filing again would create a duplicate case.',
      'pacer.prefiling.recentFilingAttempt.hardBlockDescription':
        'A filing is already in progress for this workflow. Please wait for it to complete.',
      'pacer.prefiling.existingCaseNumber.hardBlockAlert':
        'Case number {{caseNumber}} already exists. This filing is blocked.',
      'pacer.prefiling.recentFilingAttempt.hardBlockAlert': 'A filing is already in progress. This filing is blocked.',
      'pacer.filingProgress.title': 'Filing Progress',
      'pacer.filingProgress.inProgress': 'Filing in progress...',
      'pacer.filingProgress.cancel': 'Cancel',
      'pacer.filingProgress.cancelling': 'Cancelling...',
      'pacer.filingProgress.dismiss': 'Dismiss',
      // PACER error display
      'pacer.caseStatus.syncFailed': 'Case sync failed',
      'pacer.caseStatus.stepFailed': '{{stepName}} failed',
      'pacer.caseStatus.retrySync': 'Retry PACER sync',
      'pacer.caseStatus.notRetryable': 'This error requires manual intervention. Please contact support.',
      'pacer.errorCategory.timeout': "The court website didn't respond in time.",
      'pacer.errorCategory.elementNotFound': 'The court website has changed and filing could not continue.',
      'pacer.errorCategory.network': 'A network error occurred while connecting to the court.',
      'pacer.errorCategory.auth': 'PACER login failed. Please check your credentials.',
      'pacer.errorCategory.validation': 'The court rejected the filing due to a validation error.',
      'pacer.errorCategory.fileMismatch': 'An uploaded document does not match the expected format.',
      'pacer.errorCategory.infrastructure': 'An internal error occurred. Our team has been notified.',
      'pacer.errorCategory.unknown': 'An unexpected error occurred.',
      // custom statuses
      'customStatuses.usesArchiveBehavior.label': 'Use archive behavior',
      'customStatuses.usesArchiveBehavior.description':
        'When enabled, moving a workflow to this status will cancel payment plans and stop follow-ups',
      'customStatuses.disablesFollowups.label': 'Disable follow-ups',
      'customStatuses.disablesFollowups.description':
        'When enabled, automated follow-up messages will not be sent while a workflow is in this status',
      // Questionnaire linked items overview
      'questionnaire.linkedItemsOverview.title': 'Linked Items Overview',
      'questionnaire.linkedItemsOverview.noLinkedFieldsConfigured': 'No linked fields are configured for this item.',
      'questionnaire.linkedItemsOverview.noLinkedItemsOnRecord': 'No linked items selected on this record yet.',
      'questionnaire.linkedItemsOverview.noLinkedItemsSelected': 'No linked items selected.',
      'questionnaire.linkedItemsOverview.sectionLabel': 'Section: {{section}}',
      'questionnaire.linkedItemsOverview.exemptionAmountLabel': 'Exemption amount: {{value}}',
      'questionnaire.linkedItemsOverview.claimTypeLabel': 'Claim type: {{value}}',
      'questionnaire.linkedItemsOverview.totalValueOfPropertyLabel': 'Total value of the property: {{value}}',
    },
  },
};

const TranslationProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const isCreatorSlug = useMemo(() => router.pathname.startsWith('/[creatorSlug]'), [router.pathname]);
  const { profile } = useContext(AuthContext);
  const [isReadyForTranslation, setIsReadyForTranslation] = useState(false);
  const currentLanguageRef = useRef(DEFAULT_LANGUAGE.code);
  const isReadyForTranslationRef = useRef(isReadyForTranslation);
  isReadyForTranslationRef.current = isReadyForTranslation;
  const [queryLanguage, setQueryLanguage] = useQueryState('lang', parseAsStringLiteral(SUPPORTED_LANGUAGE_CODES));
  const [storedLanguage, setStoredLanguage] = useLocalStorageState<LanguageCode | null>({
    deserialize: languageSerialization.deserialize,
    key: 'language',
    prefix: 'user-global',
    serialize: languageSerialization.serialize,
  });

  // Deferred to useEffect so it doesn't run during SSR, which would cause a hydration mismatch:
  // the server would see null (no navigator) while the client would see the browser's language.
  const [systemLanguage, setSystemLanguage] = useState<LanguageCode | null>(null);
  useEffect(() => {
    const [primaryLanguage] = navigator.languages?.length ? navigator.languages : [navigator.language];
    setSystemLanguage(validateLanguageCode(primaryLanguage));
  }, []);

  const effectiveLanguage = useMemo(
    () => validateLanguageCode(queryLanguage) || storedLanguage || systemLanguage || DEFAULT_LANGUAGE.code,
    [queryLanguage, storedLanguage, systemLanguage],
  );

  // Wait for content to be loaded before translating to stored language.
  // TODO(will): A 1s timeout is not a guarantee content is loaded, this should use a real event or poll for a known element, or this variable should be renamed.
  useEffect(() => {
    const timeout = setTimeout(() => setIsReadyForTranslation(true), 1_000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const validLanguage = validateLanguageCode(profile?.preferredLanguage);
    if (validLanguage) {
      setStoredLanguage(validLanguage);
    }
  }, [profile?.preferredLanguage, setStoredLanguage]);

  useEffect(() => {
    let timeout: Parameters<typeof clearTimeout>[0];
    let observer: MutationObserver | undefined;
    if (isCreatorSlug) {
      if (isReadyForTranslation) {
        if (!isEnglishVariant(effectiveLanguage)) {
          currentLanguageRef.current = effectiveLanguage;
          handleTranslate(effectiveLanguage);
        } else if (!isEnglishVariant(currentLanguageRef.current)) {
          // Switching back to English from a non-English language — reload to remove translations.
          // @todo - must be a better way to go back to english than to reload...
          timeout = setTimeout(() => window.location.reload(), 10);
        }
      }

      // Observe the DOM for changes and re-translate.
      observer = new MutationObserver((mutations) => {
        let shouldTranslate = false;
        mutations.forEach((mutation) => {
          if (
            mutation.target.nodeName.toLowerCase() === 'textarea' ||
            mutation.target.nodeName.toLowerCase() === 'input'
          ) {
            return;
          }
          if (isReadyForTranslationRef.current && currentLanguageRef.current !== DEFAULT_LANGUAGE.code) {
            shouldTranslate = true;
          }
        });
        if (shouldTranslate) {
          handleTranslate(currentLanguageRef.current);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      clearTimeout(timeout);
      observer?.disconnect();
    };
  }, [effectiveLanguage, isReadyForTranslation, router.pathname, isCreatorSlug]);

  const setQueryLanguageWrapped = useCallback(
    (newLanguage: LanguageCode) => {
      mixpanelTrack('Changed language query string', {
        fromLanguage: currentLanguageRef.current,
        newLanguage,
      });
      const validLanguage = validateLanguageCode(newLanguage);
      if (!validLanguage) {
        logError(new Error('Invalid language code'), { newLanguage });
        return;
      }
      setQueryLanguage(validLanguage);
    },
    [setQueryLanguage],
  );

  const setLanguage = useCallback(
    async (newLanguage: LanguageCode): Promise<void> => {
      mixpanelTrack('Changed language', {
        fromLanguage: currentLanguageRef.current,
        newLanguage,
      });
      const validLanguage = validateLanguageCode(newLanguage);
      setQueryLanguage(null);
      if (!validLanguage) {
        logError(new Error('Invalid language code'), { newLanguage });
        setStoredLanguage(null);
        return;
      }

      setStoredLanguage(validLanguage);
    },
    [setStoredLanguage, setQueryLanguage],
  );

  const value = useMemo(
    () => ({
      language: effectiveLanguage,
      setLanguage,
      setQueryLanguage: setQueryLanguageWrapped,
    }),
    [effectiveLanguage, setLanguage, setQueryLanguageWrapped],
  );

  return (
    <I18nProvider resources={i18nResources} language={effectiveLanguage} persistLanguage={false}>
      <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>
    </I18nProvider>
  );
};

export default TranslationProvider;
